import { useNavigate } from "react-router-dom";
import type { RoleModuleCardData } from "@/mocks/roleModules";
import { CARD_COLORS, BADGE_COLORS } from "@/mocks/roleModules";

interface RoleModuleCardProps {
  module: RoleModuleCardData;
  index: number;
  animate?: boolean;
}

export default function RoleModuleCard({ module: mod, index, animate = true }: RoleModuleCardProps) {
  const navigate = useNavigate();
  const colorConfig = CARD_COLORS[mod.color];

  return (
    <div
      onClick={() => navigate(mod.path)}
      className="group relative bg-white border border-background-200/70 rounded-2xl p-5 flex flex-col gap-4 cursor-pointer transition-all duration-300 ease-out-expo hover:-translate-y-1 hover:border-background-300 hover:shadow-soft-lg"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      {/* Accent top bar */}
      <div className={`absolute top-0 left-4 right-4 h-1 rounded-b-full ${colorConfig.accentBar} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />

      {/* Icon */}
      <div className={`flex items-center justify-center w-12 h-12 rounded-2xl ${colorConfig.iconBg} ${colorConfig.iconText} flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-soft`}>
        <i className={`${mod.icon} text-xl`} />
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col gap-2">
        <div className="flex items-start gap-2">
          <h3 className="text-sm font-bold text-foreground-900 leading-tight">{mod.title}</h3>
          {mod.badge && (
            <span className={`flex-shrink-0 text-[10px] px-2 py-0.5 rounded-full font-semibold border ${BADGE_COLORS[mod.badge.color]} whitespace-nowrap animate-badge-pop`}>
              {mod.badge.text}
            </span>
          )}
        </div>
        <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2">{mod.description}</p>
      </div>

      {/* Action button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          navigate(mod.path);
        }}
        className={`flex items-center justify-center gap-1.5 w-full py-2.5 rounded-xl text-xs font-semibold border transition-all duration-200 cursor-pointer whitespace-nowrap ${
          mod.color === "primary" ? "bg-primary-50 text-primary-700 border-primary-200 hover:bg-primary-100"
          : mod.color === "purple" ? "bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
          : mod.color === "amber" ? "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100"
          : mod.color === "red" ? "bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
          : mod.color === "teal" ? "bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-100"
          : mod.color === "emerald" ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
          : mod.color === "sky" ? "bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-100"
          : "bg-foreground-50 text-foreground-700 border-foreground-200 hover:bg-foreground-100"
        }`}
      >
        <i className="ri-arrow-right-line text-sm" />
        Acceder al módulo
      </button>
    </div>
  );
}